(() => {
  'use strict';

  if (window.SkeinDtoVariantController) {
    window.SkeinDtoVariantController.scan();
    return;
  }

  const FROM_STOCK = 'From stock';
  const DYED_TO_ORDER = 'Dyed to order';
  const states = new Map();
  let scanFrame = null;
  let variantPickerReady = !window.customElements?.whenDefined;

  document.documentElement.dataset.dtoControllerVersion = '3.3.1';

  const contextKey = (sectionId, productId) => `${sectionId}::${productId}`;

  function getState(sectionId, productId) {
    const key = contextKey(sectionId, productId);
    if (!states.has(key)) {
      states.set(key, {
        pendingQuantity: null,
        switchingTo: null,
        restoringQuantity: false,
        switchTimer: null,
        settleTimer: null,
        switchAttempts: 0,
        switchTarget: null
      });
    }
    return states.get(key);
  }

  function getProvider(sectionId, productId) {
    return Array.from(document.querySelectorAll('[data-dto-inventory]')).find((element) => (
      element.dataset.sectionId === String(sectionId)
      && element.dataset.productId === String(productId)
    ));
  }

  function getQuantityInput(sectionId, productId) {
    return document.getElementById(`Quantity-${sectionId}-${productId}`);
  }

  function getComboPicker(sectionId, productId) {
    return document.getElementById(`VariantPickerCombo-${sectionId}-${productId}`);
  }

  function getPrimaryPicker(sectionId, productId) {
    return document.getElementById(`VariantPicker-${sectionId}-${productId}`);
  }

  function positiveNumber(value, fallback = 1) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function quantityFor(sectionId, productId) {
    const input = getQuantityInput(sectionId, productId);
    if (!input) return 1;
    return positiveNumber(input.value, positiveNumber(input.min, 1));
  }

  function capture(sectionId, productId) {
    const state = getState(sectionId, productId);
    if (!state.restoringQuantity) {
      if (state.switchTimer !== null) {
        window.clearTimeout(state.switchTimer);
        state.switchTimer = null;
      }
      if (state.settleTimer !== null) {
        window.clearTimeout(state.settleTimer);
        state.settleTimer = null;
      }
      state.pendingQuantity = quantityFor(sectionId, productId);
      state.switchingTo = null;
      state.switchAttempts = 0;
      state.switchTarget = null;
    }
    return state.pendingQuantity;
  }

  function variantCapacity(provider, prefix) {
    if (provider.dataset[`${prefix}Exists`] !== 'true') return 0;
    if (provider.dataset[`${prefix}Available`] !== 'true') return 0;

    const management = provider.dataset[`${prefix}Management`];
    const policy = provider.dataset[`${prefix}Policy`];
    const inventory = Math.max(0, Number(provider.dataset[`${prefix}Quantity`]) || 0);
    const ruleMaxValue = Number(provider.dataset[`${prefix}RuleMax`]);
    const ruleMax = Number.isFinite(ruleMaxValue) && ruleMaxValue > 0
      ? ruleMaxValue
      : Infinity;

    const inventoryCapacity = management !== 'shopify' || policy === 'continue'
      ? Infinity
      : inventory;

    return Math.min(inventoryCapacity, ruleMax);
  }

  function maximumStandaloneCapacity(provider) {
    return Math.max(
      variantCapacity(provider, 'stock'),
      variantCapacity(provider, 'dto')
    );
  }

  function selectableMaximum(input, capacity) {
    if (!Number.isFinite(capacity)) return Infinity;

    const minimum = positiveNumber(input?.min, 1);
    const increment = positiveNumber(input?.step, 1);
    if (capacity < minimum) return 0;

    return minimum + Math.floor((capacity - minimum) / increment) * increment;
  }

  function syncQuantityLimit(sectionId, productId, provider, requestedQuantity) {
    const input = getQuantityInput(sectionId, productId);
    if (!input) return requestedQuantity;

    const maximum = selectableMaximum(input, maximumStandaloneCapacity(provider));

    if (Number.isFinite(maximum)) {
      input.max = String(maximum);
      input.dataset.max = String(maximum);
      input.dataset.dtoCapacityMax = String(maximum);
    } else {
      input.removeAttribute('max');
      delete input.dataset.max;
      delete input.dataset.dtoCapacityMax;
    }

    // Never allow a requested quantity to push the matching pair into an
    // artificial sold-out state. The limit is the larger standalone capacity,
    // not the combined From-stock and DTO inventories.
    const limitedQuantity = maximum > 0
      ? Math.min(requestedQuantity, maximum)
      : requestedQuantity;

    syncQuantityButtons(input);
    return limitedQuantity;
  }

  function chooseOrderType(provider, quantity) {
    const stockCapacity = variantCapacity(provider, 'stock');
    const dtoCapacity = variantCapacity(provider, 'dto');

    if (stockCapacity >= quantity) {
      return { orderType: FROM_STOCK, fulfillable: true };
    }

    if (dtoCapacity >= quantity) {
      return { orderType: DYED_TO_ORDER, fulfillable: true };
    }

    // Keep DTO selected as the final fallback so the stock and DTO quantities
    // are never combined. The purchase controls are disabled below until the
    // requested quantity can be fulfilled by one variant on its own.
    if (provider.dataset.dtoExists === 'true') {
      return { orderType: DYED_TO_ORDER, fulfillable: false };
    }

    if (provider.dataset.stockExists === 'true') {
      return { orderType: FROM_STOCK, fulfillable: false };
    }

    return { orderType: null, fulfillable: false };
  }

  function setQuantity(sectionId, productId, quantity) {
    const input = getQuantityInput(sectionId, productId);
    if (!input) return;

    const state = getState(sectionId, productId);
    const nextValue = String(quantity);
    if (input.value !== nextValue) {
      state.restoringQuantity = true;
      input.value = nextValue;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      state.restoringQuantity = false;
    }

    syncQuantityButtons(input);
    syncStickyQuantity(sectionId, productId, nextValue);
  }

  function syncQuantityButtons(input) {
    const quantity = input.closest('quantity-input');
    if (!quantity) return;

    const value = positiveNumber(input.value, positiveNumber(input.min, 1));
    const min = positiveNumber(input.min, 1);
    const maxValue = Number(input.max);
    const max = input.hasAttribute('max') && Number.isFinite(maxValue)
      ? maxValue
      : Infinity;
    const minus = quantity.querySelector('button[name="minus"]');
    const plus = quantity.querySelector('button[name="plus"]');

    if (minus) minus.disabled = value <= min;
    if (plus) plus.disabled = value >= max;
  }

  function adjustQuantity(input, direction) {
    const previousValue = input.value;

    try {
      if (direction === 'plus') {
        input.stepUp();
      } else {
        input.stepDown();
      }
    } catch (error) {
      const step = positiveNumber(input.step, 1);
      const current = positiveNumber(input.value, positiveNumber(input.min, 1));
      const min = positiveNumber(input.min, 1);
      const maxValue = Number(input.max);
      const max = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : Infinity;
      input.value = String(Math.min(max, Math.max(min, current + (direction === 'plus' ? step : -step))));
    }

    syncQuantityButtons(input);

    if (input.value !== previousValue) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function syncStickyQuantity(sectionId, productId, quantity) {
    const sticky = document.getElementById(`ProductStickyForm-${sectionId}-${productId}`);
    if (!sticky) return;

    sticky.querySelectorAll('input[name="mini-quantity"]').forEach((input) => {
      input.value = quantity;
    });
  }

  function setButtonSoldOut(button, soldOut) {
    const text = button.querySelector('[data-sold-out]');

    if (soldOut) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.dtoQuantityUnavailable = 'true';

      if (text) {
        if (!text.dataset.dtoOriginalText) {
          text.dataset.dtoOriginalText = text.textContent.trim();
        }
        if (text.textContent !== text.dataset.soldOut) {
          text.textContent = text.dataset.soldOut;
        }
      }
      return;
    }

    if (button.dataset.dtoQuantityUnavailable === 'true') {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      delete button.dataset.dtoQuantityUnavailable;

      if (text?.dataset.dtoOriginalText) {
        text.textContent = text.dataset.dtoOriginalText;
        delete text.dataset.dtoOriginalText;
      }
    }
  }

  function syncPurchaseState(sectionId, productId, variantId, currentAvailable, fulfillable) {
    const formId = `ProductForm-${sectionId}-${productId}`;
    const form = document.getElementById(formId);
    const canPurchase = currentAvailable && fulfillable;

    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && variantId) {
        variantInput.value = variantId;
        variantInput.setAttribute('value', variantId);
        variantInput.dataset.dtoResolvedVariantId = variantId;
        variantInput.disabled = !canPurchase;
      }

      const quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput && variantId) {
        quantityInput.dataset.quantityVariantId = variantId;
      }

      form.querySelectorAll('button[type="submit"][name="add"]').forEach((button) => {
        setButtonSoldOut(button, !canPurchase);
      });
    }

    document.querySelectorAll('button[type="submit"][name="add"][form]').forEach((button) => {
      if (button.getAttribute('form') === formId) {
        setButtonSoldOut(button, !canPurchase);
      }
    });
  }

  function orderTypeInput(sectionId, productId, orderType) {
    const combo = getComboPicker(sectionId, productId);
    if (!combo) return null;

    return Array.from(combo.querySelectorAll('[data-dto-order-type]')).find((input) => (
      input.value === orderType
    )) || null;
  }

  function selectedOrderType(sectionId, productId, provider) {
    const combo = getComboPicker(sectionId, productId);
    return combo?.querySelector('[data-dto-order-type]:checked')?.value
      || provider.dataset.currentOrderType;
  }

  function targetVariant(provider, orderType) {
    const prefix = orderType === DYED_TO_ORDER ? 'dto' : 'stock';
    return {
      id: provider.dataset[`${prefix}VariantId`] || '',
      available: provider.dataset[`${prefix}Available`] === 'true'
    };
  }

  function optionsAreSettled(provider, combo) {
    const selectedColour = combo.querySelector('[data-combo-colour]:checked')?.value || '';
    const selectedSize = combo.querySelector('[data-combo-size]')?.value || '';
    const providerColour = provider.dataset.currentColour || '';
    const providerSize = provider.dataset.currentSize || '';

    if (selectedColour && providerColour && selectedColour !== providerColour) return false;
    if (selectedSize && providerSize && selectedSize !== providerSize) return false;
    return true;
  }

  function scheduleOrderTypeSwitch(sectionId, productId, orderType) {
    const state = getState(sectionId, productId);

    if (state.switchTarget !== orderType) {
      state.switchTarget = orderType;
      state.switchAttempts = 0;
    }

    // Allow a stale theme response to be repaired without risking a permanent
    // request loop if the picker cannot render the requested variant.
    if (state.switchAttempts >= 3) return;

    if (state.switchTimer !== null) {
      window.clearTimeout(state.switchTimer);
    }

    state.switchingTo = orderType;
    state.switchTimer = window.setTimeout(() => {
      state.switchTimer = null;

      const provider = getProvider(sectionId, productId);
      const combo = getComboPicker(sectionId, productId);
      if (!provider || !combo || !optionsAreSettled(provider, combo)) {
        state.switchingTo = null;
        return;
      }

      const quantity = positiveNumber(
        state.pendingQuantity,
        quantityFor(sectionId, productId)
      );
      const choice = chooseOrderType(provider, quantity);
      if (choice.orderType !== orderType) {
        state.switchingTo = null;
        reconcile(sectionId, productId);
        return;
      }

      const input = orderTypeInput(sectionId, productId, orderType);
      const target = targetVariant(provider, orderType);
      const providerNeedsUpdate = provider.dataset.currentOrderType !== orderType;
      state.switchAttempts += 1;
      if (input && (!input.checked || providerNeedsUpdate)) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncPurchaseState(sectionId, productId, target.id, target.available, choice.fulfillable);

      if (state.settleTimer !== null) {
        window.clearTimeout(state.settleTimer);
      }
      state.settleTimer = window.setTimeout(() => {
        state.settleTimer = null;
        state.switchingTo = null;
        reconcile(sectionId, productId);
      }, 250);
    }, 80);
  }

  function syncSwatches(sectionId, productId) {
    const combo = getComboPicker(sectionId, productId);
    const primary = getPrimaryPicker(sectionId, productId);
    if (!combo || !primary) return;

    const primaryColourOption = primary.querySelector('[data-option-slug="colour"]');
    const select = primaryColourOption?.querySelector('select');
    if (!select) return;

    // A DTO selection can legitimately leave the theme's separate Colour
    // dropdown with no rendered options. In that state its value is empty,
    // but the combo picker still has the correct checked Colour radio from
    // Liquid. Never clear that valid selection just because the other picker
    // is empty.
    if (select.value) {
      combo.querySelectorAll('[data-combo-colour]').forEach((input) => {
        input.checked = input.value === select.value;
      });
    }

    const primarySize = primary.querySelector('[data-option-slug="size"] select');
    const comboSize = combo.querySelector('[data-combo-size]');
    if (primarySize && comboSize) {
      comboSize.value = primarySize.value;
    }

    const inStockOnly = document.getElementById('in_stock_only');
    if (!inStockOnly) return;

    combo.querySelectorAll('.unavailable').forEach((item) => {
      const hidden = inStockOnly.checked;
      item.hidden = hidden;
      item.style.display = hidden ? 'none' : 'block';
    });
  }

  function forwardPrimaryOption(sectionId, productId, optionSlug, value) {
    const combo = getComboPicker(sectionId, productId);
    if (!combo || !value) return false;

    let target = null;
    if (optionSlug === 'size') {
      target = combo.querySelector('[data-combo-size]');
    } else if (optionSlug === 'colour') {
      target = Array.from(combo.querySelectorAll('[data-combo-colour]')).find((input) => (
        input.value === value
      )) || null;
    }

    if (!target) return false;

    if (target.type === 'radio') {
      target.checked = true;
    } else {
      target.value = value;
    }

    target.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function reconcile(sectionId, productId) {
    const provider = getProvider(sectionId, productId);
    const combo = getComboPicker(sectionId, productId);
    if (!provider && !combo) return;

    const state = getState(sectionId, productId);
    let quantity = positiveNumber(
      state.pendingQuantity,
      quantityFor(sectionId, productId)
    );

    if (!provider) {
      // Non-DTO products can still use the combined Size/Colour picker for
      // quantity preservation and option forwarding. Their selected variant,
      // availability and purchase button remain entirely theme-controlled.
      setQuantity(sectionId, productId, quantity);
      return;
    }

    if (combo && !optionsAreSettled(provider, combo)) return;

    quantity = syncQuantityLimit(
      sectionId,
      productId,
      provider,
      quantity
    );
    state.pendingQuantity = quantity;
    setQuantity(sectionId, productId, quantity);

    const choice = chooseOrderType(provider, quantity);
    const target = targetVariant(provider, choice.orderType);

    if (!variantPickerReady) return;

    const selectedType = selectedOrderType(sectionId, productId, provider);
    const providerTypeSettled = provider.dataset.currentOrderType === choice.orderType;

    if (choice.orderType && (choice.orderType !== selectedType || !providerTypeSettled)) {
      if (state.switchingTo === choice.orderType) {
        syncPurchaseState(sectionId, productId, target.id, target.available, choice.fulfillable);
        return;
      }

      scheduleOrderTypeSwitch(sectionId, productId, choice.orderType);
      syncPurchaseState(sectionId, productId, target.id, target.available, choice.fulfillable);
      return;
    }

    state.switchingTo = null;
    state.switchAttempts = 0;
    state.switchTarget = choice.orderType;
    syncPurchaseState(sectionId, productId, target.id, target.available, choice.fulfillable);
  }

  function scan() {
    const contexts = new Map();

    document.querySelectorAll('[data-combo-picker], [data-dto-inventory]').forEach((element) => {
      if (element.dataset.sectionId && element.dataset.productId) {
        contexts.set(
          contextKey(element.dataset.sectionId, element.dataset.productId),
          [element.dataset.sectionId, element.dataset.productId]
        );
      }
    });

    contexts.forEach(([sectionId, productId]) => {
      reconcile(sectionId, productId);
    });
  }

  function scheduleScan() {
    if (scanFrame !== null) return;
    scanFrame = window.requestAnimationFrame(() => {
      scanFrame = null;
      scan();
    });
  }

  function scheduleContext(sectionId, productId) {
    window.requestAnimationFrame(() => reconcile(sectionId, productId));
  }

  function contextFromQuantityElement(element) {
    const quantity = element.closest('quantity-input[data-section-id][data-product-id]');
    if (!quantity) return null;
    return {
      sectionId: quantity.dataset.sectionId,
      productId: quantity.dataset.productId
    };
  }

  document.addEventListener('change', (event) => {
    const colourInput = event.target.closest?.('[data-combo-colour]');
    if (colourInput) {
      const combo = colourInput.closest('[data-combo-picker]');
      if (!combo) return;

      capture(combo.dataset.sectionId, combo.dataset.productId);

      // Combined-listing swatches are navigational and should remain under the
      // theme's control.
      if (colourInput.dataset.productUrl) return;

      // The combo element is a real variant-picker and already contains the
      // selected Colour, optional Size and hidden Order Type. Let the theme
      // process this change itself. Forwarding it through the separate
      // dropdown breaks as soon as that dropdown has no options in DTO state.
      return;
    }

    if (event.target.closest?.('[data-dto-order-type]')) return;

    const primaryPicker = event.target.closest?.('[id^="VariantPicker-"]');
    if (primaryPicker && !primaryPicker.hasAttribute('data-combo-picker')) {
      const context = Array.from(document.querySelectorAll('[data-combo-picker], [data-dto-inventory]')).find((item) => (
        primaryPicker.id === `VariantPicker-${item.dataset.sectionId}-${item.dataset.productId}`
      ));
      if (context) {
        capture(context.dataset.sectionId, context.dataset.productId);

        const optionSlug = event.target.closest?.('[data-option-slug]')?.dataset.optionSlug;
        if (
          event.target.matches?.('select')
          && (optionSlug === 'size' || optionSlug === 'colour')
          && forwardPrimaryOption(
            context.dataset.sectionId,
            context.dataset.productId,
            optionSlug,
            event.target.value
          )
        ) {
          // The combo picker is canonical because it contains Colour, optional
          // Size and the hidden Order Type together. Prevent the duplicate
          // dropdown picker from starting a competing section request.
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }
    }
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('quantity-input button[name="plus"], quantity-input button[name="minus"]');
    if (!button) return;

    const context = contextFromQuantityElement(button);
    if (!context) return;

    const quantityBeforeClick = quantityFor(context.sectionId, context.productId);

    window.setTimeout(() => {
      const state = getState(context.sectionId, context.productId);
      if (state.restoringQuantity) return;

      const input = getQuantityInput(context.sectionId, context.productId);
      if (input && quantityFor(context.sectionId, context.productId) === quantityBeforeClick) {
        // Some theme variant renders replace <quantity-input> without restoring
        // its button listeners. Fall back only when the theme did not change
        // the value itself, so a working native handler is never doubled.
        adjustQuantity(input, button.name);
      }

      capture(context.sectionId, context.productId);
      scheduleContext(context.sectionId, context.productId);
    }, 100);
  }, true);

  document.addEventListener('input', (event) => {
    if (!event.target.matches?.('quantity-input input[name="quantity"]')) return;
    if (!event.isTrusted) return;

    const context = contextFromQuantityElement(event.target);
    if (!context) return;

    const state = getState(context.sectionId, context.productId);
    if (state.restoringQuantity) return;

    capture(context.sectionId, context.productId);
    syncStickyQuantity(context.sectionId, context.productId, event.target.value);
    scheduleContext(context.sectionId, context.productId);
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('quantity-input input[name="quantity"]')) return;
    if (!event.isTrusted) return;

    const context = contextFromQuantityElement(event.target);
    if (!context) return;

    const state = getState(context.sectionId, context.productId);
    if (state.restoringQuantity) return;

    capture(context.sectionId, context.productId);
    scheduleContext(context.sectionId, context.productId);
  });

  document.addEventListener('variant:change', scheduleScan);
  document.addEventListener('page:loaded', scheduleScan);
  document.addEventListener('shopify:section:load', scheduleScan);

  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) {
      scheduleScan();
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.SkeinDtoVariantController = {
    capture,
    reconcile,
    scan,
    syncSwatches
  };

  if (window.customElements?.whenDefined) {
    window.customElements.whenDefined('variant-picker').then(() => {
      variantPickerReady = true;
      states.forEach((state) => {
        state.switchingTo = null;
      });
      scheduleScan();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }
})();
